import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { PublicKey, Transaction } from '@solana/web3.js';
import {
  createTransferCheckedInstruction,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import bs58 from 'bs58';
import { apiFetch, getApiHost } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent, trackError } from '../utils/analytics';
import { redirectToPhantomIfMobile } from '../utils/phantomMobile';
import {
  persistentPhantomWalletMatches,
  startPhantomSignTransaction,
  consumePhantomTxResult,
} from '../utils/phantomDeeplink';
import {
  hasLiveWCSessionSync,
  signTransactionViaWC,
} from '../utils/walletConnectMobile';

// Mirrors CryptoBuyCredits.tsx — same wallet status pre-check, same
// SPL transferChecked → /api/burn/send-transaction → backend verify
// pattern. Difference: a single "1 month" bundle per tier (not a list
// of credit packs), and the backend extends or replaces a sub period
// rather than granting credits.

type Tier = 'watcher' | 'deep_diver';

interface CryptoSubPricing {
  usdcMint: string;
  usdcDecimals: number;
  payTo: string;
  payToUsdcAta: string;
  tiers: Record<Tier, { usdcMonthly: number; usdcMonthlyAtomic: number; periodDays: number }>;
}

interface Props {
  tier: Tier;
  onPaid?: () => void;
  onCancel?: () => void;
}

const tierLabel = (t: Tier) => (t === 'watcher' ? 'Watcher' : 'Deep Diver');
const tierIcon  = (t: Tier) => (t === 'watcher' ? '👁️' : '🔱');

const CryptoSubscriptionPay: React.FC<Props> = ({ tier, onPaid, onCancel }) => {
  const { publicKey, signTransaction } = useWallet();
  const { setVisible: setWalletModalVisible } = useWalletModal();
  const { user, refreshUser } = useAuth();

  const authWalletAddress = user?.walletAddress ?? null;
  const displayWalletAddress = publicKey?.toBase58() ?? authWalletAddress;

  const [pricing, setPricing] = useState<CryptoSubPricing | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [loadingPricing, setLoadingPricing] = useState(true);

  const [paying, setPaying] = useState(false);
  const [payStatus, setPayStatus] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [paySuccess, setPaySuccess] = useState<{ currentPeriodEnd: string; extended: boolean; txSignature: string } | null>(null);

  // Fire SPA-visible failure event for each new payError. Caught by
  // any of the multiple error paths (rejected sign, no USDC, verify
  // failure, etc.). Useful for funnel analysis — server-side
  // subscription_started only counts successes, this captures the
  // drop-off shape.
  useEffect(() => {
    if (payError) {
      trackEvent('crypto_sub_pay_failed', { tier, reason: payError });
    }
  }, [payError, tier]);

  const [usdcBalance, setUsdcBalance] = useState<number | null>(null);
  const [usdcAtaExists, setUsdcAtaExists] = useState<boolean | null>(null);

  const fetchPricing = useCallback(async () => {
    try {
      setLoadingPricing(true);
      setPricingError(null);
      const res = await apiFetch(`${getApiHost()}/api/crypto-sub/price`);
      if (!res.ok) throw new Error('Failed to fetch pricing');
      setPricing(await res.json());
    } catch (err) {
      setPricingError(err instanceof Error ? err.message : 'Failed to load pricing');
    } finally {
      setLoadingPricing(false);
    }
  }, []);

  useEffect(() => { fetchPricing(); }, [fetchPricing]);

  const walletAddress = displayWalletAddress;
  useEffect(() => {
    if (!walletAddress) { setUsdcBalance(null); setUsdcAtaExists(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${getApiHost()}/api/crypto-sub/wallet-status/${walletAddress}`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setUsdcBalance(data.balanceUsdc);
        setUsdcAtaExists(data.ataExists);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [walletAddress]);

  // Submit a base64-serialized signed tx through the API proxies, then
  // verify + activate the subscription. Used by both the wallet-adapter
  // path and the deeplink-callback / WC continuations.
  const submitSignedTx = useCallback(async (serializedBase64: string) => {
    setPayStatus('Sending transaction…');
    const sendRes = await apiFetch(`${getApiHost()}/api/burn/send-transaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transaction: serializedBase64 }),
    });
    if (!sendRes.ok) {
      const errData = await sendRes.json().catch(() => ({}));
      throw new Error(errData.error || 'Failed to send transaction');
    }
    const { signature } = await sendRes.json();

    setPayStatus('Verifying payment and activating subscription…');
    const token = localStorage.getItem('deepnets_token');
    const verifyRes = await apiFetch(`${getApiHost()}/api/crypto-sub/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ txSignature: signature, tier }),
    });
    if (!verifyRes.ok) {
      const errData = await verifyRes.json().catch(() => ({}));
      throw new Error(errData.error || 'Verification failed');
    }
    const result = await verifyRes.json();
    setPaySuccess({
      currentPeriodEnd: result.currentPeriodEnd,
      extended: !!result.extended,
      txSignature: signature,
    });
    setPayStatus(null);
    trackEvent('crypto_sub_pay_succeeded', {
      tier,
      extended: !!result.extended,
      txSignature: signature,
    });
    await refreshUser();
    onPaid?.();
  }, [tier, refreshUser, onPaid]);

  // Pick up Phantom-deeplink-tx continuations on mount. The receiving
  // page (TiersPage) re-opens the picker for the right tier; this
  // component then mounts and consumes the result.
  const continuationRanRef = useRef(false);
  useEffect(() => {
    if (continuationRanRef.current) return;
    if (!new URLSearchParams(window.location.search).has('phantom-tx-pending')) return;
    continuationRanRef.current = true;

    const tx = consumePhantomTxResult('buy-subscription');
    if (!tx) return;
    // Strip the flag + tier hint so refreshes don't re-fire.
    const url = new URL(window.location.href);
    url.searchParams.delete('phantom-tx-pending');
    url.searchParams.delete('tier');
    window.history.replaceState({}, '', url.toString());

    if (tx.error) {
      setPayError(tx.error.toLowerCase().includes('cancel') ? 'Transaction cancelled' : tx.error);
      setPaying(false);
      return;
    }
    if (!tx.signedTransactionBase58) {
      setPayError('Phantom returned no signed transaction');
      setPaying(false);
      return;
    }
    const txBytes = bs58.decode(tx.signedTransactionBase58);
    const base64 = btoa(String.fromCharCode(...txBytes));
    setPaying(true);
    setPayError(null);
    setPaySuccess(null);
    submitSignedTx(base64).catch(err => {
      const msg = err?.message || 'Payment failed';
      if (!msg.includes('User rejected')) trackError(err, { flow: 'crypto_subscription_pay' });
      setPayError(msg.includes('User rejected') ? 'Transaction cancelled' : msg);
      setPayStatus(null);
    }).finally(() => setPaying(false));
  }, [submitSignedTx]);

  const handlePay = async () => {
    if (!pricing) return;

    const tierPrice = pricing.tiers[tier];
    if (!tierPrice) return;

    trackEvent('crypto_sub_pay_clicked', { tier, amountUsd: tierPrice.usdcMonthly });

    // Mobile fast path #1: persistent Phantom session for the same auth
    // wallet → bounce to Phantom signTransaction (page reload, picked
    // up via continuation hook above when TiersPage re-opens the modal).
    const usePhantomDeeplink = !publicKey && persistentPhantomWalletMatches(authWalletAddress);
    if (usePhantomDeeplink) {
      try {
        setPaying(true);
        setPayError(null);
        setPaySuccess(null);
        setPayStatus('Building transaction…');
        const burnerPubkey = new PublicKey(authWalletAddress!);
        const usdcMint = new PublicKey(pricing.usdcMint);
        const destAta = new PublicKey(pricing.payToUsdcAta);
        const sourceAta = await getAssociatedTokenAddress(usdcMint, burnerPubkey, false, TOKEN_PROGRAM_ID);

        const transferIx = createTransferCheckedInstruction(
          sourceAta, usdcMint, destAta, burnerPubkey,
          BigInt(tierPrice.usdcMonthlyAtomic), pricing.usdcDecimals, [], TOKEN_PROGRAM_ID,
        );

        const blockhashRes = await apiFetch(`${getApiHost()}/api/burn/blockhash`);
        if (!blockhashRes.ok) throw new Error('Could not fetch blockhash');
        const { blockhash } = await blockhashRes.json();

        const tx = new Transaction().add(transferIx);
        tx.recentBlockhash = blockhash;
        tx.feePayer = burnerPubkey;

        const transactionBase58 = bs58.encode(tx.serialize({ requireAllSignatures: false }));
        // Encode tier in the returnUrl so TiersPage can re-open the
        // upgrade modal at the crypto pay step for the right tier.
        const returnUrl = `/tiers?tier=${tier}`;
        startPhantomSignTransaction({
          transactionBase58,
          operation: 'buy-subscription',
          params: { tier },
          returnUrl,
        });
        return;
      } catch (err: any) {
        setPayError(err?.message || 'Payment failed');
        setPayStatus(null);
        setPaying(false);
        return;
      }
    }

    // Mobile fast path #2: live WC session → sign over relay, await on
    // the same page (no continuation hop).
    const useWalletConnect = !publicKey && !!authWalletAddress && hasLiveWCSessionSync(authWalletAddress);
    if (useWalletConnect) {
      setPaying(true);
      setPayError(null);
      setPaySuccess(null);
      setPayStatus('Building transaction…');
      try {
        const burnerPubkey = new PublicKey(authWalletAddress!);
        const usdcMint = new PublicKey(pricing.usdcMint);
        const destAta = new PublicKey(pricing.payToUsdcAta);
        const sourceAta = await getAssociatedTokenAddress(usdcMint, burnerPubkey, false, TOKEN_PROGRAM_ID);

        const transferIx = createTransferCheckedInstruction(
          sourceAta, usdcMint, destAta, burnerPubkey,
          BigInt(tierPrice.usdcMonthlyAtomic), pricing.usdcDecimals, [], TOKEN_PROGRAM_ID,
        );

        const blockhashRes = await apiFetch(`${getApiHost()}/api/burn/blockhash`);
        if (!blockhashRes.ok) throw new Error('Could not fetch blockhash');
        const { blockhash } = await blockhashRes.json();

        const tx = new Transaction().add(transferIx);
        tx.recentBlockhash = blockhash;
        tx.feePayer = burnerPubkey;

        const transactionBase58 = bs58.encode(tx.serialize({ requireAllSignatures: false }));
        setPayStatus('Approve in your wallet…');
        const signedBytes = await signTransactionViaWC(transactionBase58);
        const base64 = btoa(String.fromCharCode(...signedBytes));
        await submitSignedTx(base64);
      } catch (err: any) {
        const msg = err?.message || 'Payment failed';
        const looksLikeNoUsdc = msg.includes('InvalidAccountData') || msg.includes('invalid account data');
        if (msg.toLowerCase().includes('reject') || msg.toLowerCase().includes('cancel')) {
          setPayError('Transaction cancelled');
        } else if (looksLikeNoUsdc) {
          setPayError('No USDC found in this wallet. Fund it with USDC on Solana, then try again.');
        } else {
          setPayError(msg);
        }
        setPayStatus(null);
      } finally {
        setPaying(false);
      }
      return;
    }

    // Desktop / extension wallet path.
    if (!publicKey || !signTransaction) {
      if (authWalletAddress) {
        setPayError(
          'No active wallet session for signing. Sign out and sign in again with the wallet you want to pay from.'
        );
        return;
      }
      if (redirectToPhantomIfMobile()) return;
      setWalletModalVisible(true);
      return;
    }
    if (authWalletAddress && publicKey.toBase58() !== authWalletAddress) {
      setPayError(
        `Connected wallet doesn't match the wallet on your account (${authWalletAddress.slice(0, 4)}…${authWalletAddress.slice(-4)})`
      );
      return;
    }

    setPaying(true);
    setPayError(null);
    setPaySuccess(null);
    setPayStatus('Building transaction…');

    try {
      const usdcMint = new PublicKey(pricing.usdcMint);
      const destAta = new PublicKey(pricing.payToUsdcAta);
      const sourceAta = await getAssociatedTokenAddress(usdcMint, publicKey, false, TOKEN_PROGRAM_ID);

      const transferIx = createTransferCheckedInstruction(
        sourceAta, usdcMint, destAta, publicKey,
        BigInt(tierPrice.usdcMonthlyAtomic), pricing.usdcDecimals, [], TOKEN_PROGRAM_ID,
      );

      setPayStatus('Preparing transaction…');
      const blockhashRes = await apiFetch(`${getApiHost()}/api/burn/blockhash`);
      if (!blockhashRes.ok) throw new Error('Could not fetch blockhash');
      const { blockhash } = await blockhashRes.json();

      const tx = new Transaction().add(transferIx);
      tx.recentBlockhash = blockhash;
      tx.feePayer = publicKey;

      setPayStatus('Approve the USDC payment in your wallet…');
      const signed = await signTransaction(tx);

      const serialized = signed.serialize().toString('base64');
      await submitSignedTx(serialized);
    } catch (err: any) {
      console.error('Crypto sub pay error:', err);
      const msg = err?.message || 'Payment failed';
      const looksLikeNoUsdc = msg.includes('InvalidAccountData') || msg.includes('invalid account data');
      if (msg.includes('User rejected') || msg.includes('rejected the request')) {
        setPayError('Transaction cancelled');
      } else if (looksLikeNoUsdc) {
        setPayError('No USDC found in this wallet. Fund it with USDC on Solana, then try again.');
      } else if (msg.toLowerCase().includes('insufficient')) {
        setPayError(`${msg} — make sure your wallet has enough USDC and a small SOL balance for gas.`);
      } else {
        setPayError(msg);
      }
      setPayStatus(null);
    } finally {
      setPaying(false);
    }
  };

  if (loadingPricing) {
    return (
      <div className="text-center py-6">
        <div className="animate-spin w-6 h-6 border-2 border-crypto-accent border-t-transparent rounded-full mx-auto mb-2"></div>
        <p className="text-gray-400 text-sm">Loading pricing…</p>
      </div>
    );
  }

  if (pricingError || !pricing) {
    return (
      <div className="text-center py-6">
        <p className="text-red-400 text-sm mb-2">Could not load pricing</p>
        <button onClick={fetchPricing} className="text-crypto-accent hover:text-crypto-accent/80 text-sm">Try again</button>
      </div>
    );
  }

  const price = pricing.tiers[tier];
  const noUsdcAtAll = usdcAtaExists === false;
  const insufficientUsdc = usdcBalance != null && price && usdcBalance < price.usdcMonthly;

  // Existing crypto-sub state for this user (if any) — surface so they
  // can see whether they're extending or starting fresh.
  const existing = user?.cryptoSubscription as
    | { tier: Tier; currentPeriodEnd: string; active: boolean }
    | undefined;
  const existingActive = !!existing?.active;
  const sameTierExtension = existingActive && existing?.tier === tier;

  return (
    <div className="space-y-4">
      <div className="text-center">
        <div className="text-3xl mb-1">{tierIcon(tier)}</div>
        <div className="text-white font-semibold">{tierLabel(tier)} — 1 month</div>
        <div className="text-gray-400 text-sm">{price.usdcMonthly.toFixed(2)} USDC, no auto-renewal</div>
      </div>

      {existingActive && (
        <div className="text-xs px-3 py-2 rounded-lg border bg-crypto-dark border-gray-700 text-gray-300">
          {sameTierExtension
            ? `Extends your current ${tierLabel(existing!.tier)} crypto sub. Period will run through ${new Date(new Date(existing!.currentPeriodEnd).getTime() + price.periodDays * 86400000).toLocaleDateString()}.`
            : `You currently have an active ${tierLabel(existing!.tier)} crypto sub through ${new Date(existing!.currentPeriodEnd).toLocaleDateString()}. Paying for ${tierLabel(tier)} resets the period to ${price.periodDays} days from now.`}
        </div>
      )}

      {walletAddress && usdcBalance != null && (
        <div className={`text-xs px-3 py-2 rounded-lg border ${
          noUsdcAtAll || insufficientUsdc
            ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
            : 'bg-crypto-dark border-gray-700 text-gray-400'
        }`}>
          {noUsdcAtAll
            ? 'No USDC found in this wallet. Fund it with USDC on Solana before paying.'
            : `Wallet balance: ${usdcBalance.toFixed(2)} USDC`}
        </div>
      )}

      <button
        onClick={handlePay}
        disabled={paying || !displayWalletAddress || noUsdcAtAll || !!insufficientUsdc}
        className="w-full bg-crypto-accent hover:bg-crypto-accent/80 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-crypto-dark font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        {paying ? (
          <>
            <div className="animate-spin w-4 h-4 border-2 border-crypto-dark border-t-transparent rounded-full" />
            <span>{payStatus || 'Processing…'}</span>
          </>
        ) : (
          <>
            <span>🪙</span>
            <span>
              {sameTierExtension ? 'Extend' : 'Pay'} {price.usdcMonthly.toFixed(2)} USDC for 1 month
            </span>
          </>
        )}
      </button>

      {!displayWalletAddress && (
        <p className="text-gray-500 text-xs text-center">Connect your Solana wallet to pay with USDC</p>
      )}

      {payError && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{payError}</p>
        </div>
      )}

      {paySuccess && (
        <div className="p-3 bg-crypto-accent/10 border border-crypto-accent/30 rounded-lg">
          <p className="text-crypto-accent text-sm font-medium">
            ✓ {paySuccess.extended ? `${tierLabel(tier)} extended` : `${tierLabel(tier)} active`} until {new Date(paySuccess.currentPeriodEnd).toLocaleDateString()}
          </p>
          <a
            href={`https://solscan.io/tx/${paySuccess.txSignature}`}
            target="_blank" rel="noopener noreferrer"
            className="text-gray-400 hover:text-white text-xs mt-1 block transition-colors"
          >
            View transaction →
          </a>
        </div>
      )}

      {onCancel && (
        <button
          onClick={onCancel}
          className="w-full text-xs text-gray-500 hover:text-gray-300 py-1"
        >
          ← Back
        </button>
      )}

      <p className="text-gray-600 text-xs text-center">
        USDC on Solana mainnet. You'll need a small SOL balance for the network fee (~$0.001).
        Pay again before the period ends to extend.
      </p>
    </div>
  );
};

export default CryptoSubscriptionPay;
